import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, useData } from "@/lib/api";

export default function ContactFiles({ contactId, onChange }) {
  const [files, reload] = useData(`/contacts/${contactId}/files`, []);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/contacts/${contactId}/files`, fd);
      toast.success("File attached");
      reload();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      input.current.value = "";
    }
  };

  const download = async (f) => {
    const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.original_filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (f) => {
    await api.delete(`/files/${f.id}`);
    toast.success("File removed");
    reload();
    onChange?.();
  };

  return (
    <div className="space-y-3" data-testid="contact-files">
      <input ref={input} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="contact-file-input" />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current.click()} className="border-[#D4AF37]" data-testid="contact-file-upload-btn">
        <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading…" : "Attach file"}
      </Button>
      {files.length === 0 && <p className="text-xs text-stone-500">No files attached yet (max 10 MB each).</p>}
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-2 rounded-lg border p-2 text-sm" data-testid={`contact-file-${f.id}`}>
          <Paperclip className="h-4 w-4 text-[#3B185F]" />
          <span className="flex-1 truncate">{f.original_filename}</span>
          <span className="text-xs text-stone-400">{Math.ceil(f.size / 1024)} KB</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(f)} data-testid={`contact-file-download-${f.id}`}><Download className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(f)} data-testid={`contact-file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}
